import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Phone, ChatText, List, X, CaretDown, Star, ShieldCheck, Lightning, Scales, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BookingDialog } from "@/components/BookingDialog";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLAR_ICONS = [Lightning, ShieldCheck, Scales, Sparkle];

const Reveal = ({ children, delay = 0, y = 28, className, ...rest }) => (
  <motion.div className={className} initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }} {...rest}>
    {children}
  </motion.div>
);

const AutoPauseVideo = ({ src, testid }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting && !el.paused) el.pause();
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <video ref={ref} src={`${src}#t=0.1`} controls preload="metadata" playsInline className="w-full" data-testid={testid} />;
};

const Kicker = ({ children }) => (
  <div className="flex items-center gap-3 mb-4">
    <motion.span initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }}
      transition={{ duration: 0.5, ease: "easeOut" }} className="h-[2px] w-8 bg-[#FFE01A] origin-left" />
    <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#FFE01A]">{children}</span>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-condensed uppercase font-bold text-white tracking-tight leading-[0.95] text-4xl sm:text-5xl max-w-3xl">{children}</h2>
);

export default function LandingPage() {
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [floatHidden, setFloatHidden] = useState(false);
  const sapRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/site/content`).then(({ data }) => setC(data)).catch(() => setC({}));
  }, []);

  useEffect(() => {
    if (!c?.seo) return;
    document.title = c.seo.title || "The Self-Agency Project";
    let m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", c.seo.description || "");
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "e650-ldjson";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "LocalBusiness",
      name: "The Self-Agency Project — by Elevation650",
      description: c.seo.description, url: window.location.origin,
      telephone: c.contact?.phone_raw, areaServed: "Alberta, Canada",
      priceRange: "$48-$100",
    });
    document.getElementById("e650-ldjson")?.remove();
    document.head.appendChild(ld);
    return () => document.getElementById("e650-ldjson")?.remove();
  }, [c]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("book")) setBookingOpen(true);
    const sid = q.get("pack_session");
    if (!sid) return;
    window.history.replaceState({}, "", "/");
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/site/packages/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success(`${data.sessions} session credits added! Book anytime with your email — no payment needed.`, { duration: 8000 });
          setBookingOpen(true);
          return;
        }
      } catch { /* retry */ }
      if (++tries < 12) setTimeout(poll, 2500);
    };
    poll();
  }, []);

  useEffect(() => {
    const el = sapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setFloatHidden(true);
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [c]);

  if (!c) return <div className="min-h-screen bg-[#0A0A0A]" />;

  const phoneHref = `tel:${c.contact?.phone_raw || ""}`;
  const smsHref = `sms:${c.contact?.phone_raw || ""}`;
  const goApp = () => nav("/welcome");
  const scrollTo = (id) => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };

  const ctaAction = (kind) => {
    if (kind === "app") return goApp;
    if (kind === "book") return () => setBookingOpen(true);
    return () => { window.location.href = phoneHref; };
  };

  const sendContact = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 1 || form.message.trim().length < 3) return;
    setSending(true);
    try {
      await axios.post(`${API}/site/contact`, form);
      toast.success("Message sent — we'll get back to you soon!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Couldn't send — please call or text instead.");
    } finally { setSending(false); }
  };

  const navLinks = [
    ["self-agency", "Self-Agency Project"], ["system", "The System"], ["pricing", "Pricing"], ["faqs", "FAQs"], ["results", "Results"], ["contact", "Contact"],
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="landing-page">
      <style>{`
        .font-anton{font-family:'Anton',sans-serif}
        .font-condensed{font-family:'Barlow Condensed',sans-serif}
        .e650-grain{position:relative}
        .e650-grain::after{content:"";position:absolute;inset:0;pointer-events:none;opacity:.04;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")}
        .e650-card{transition:transform .2s ease,border-color .2s ease,box-shadow .2s ease}
        .e650-card:hover{transform:translate(-2px,-2px);border-color:#FFE01A;box-shadow:6px 6px 0 rgba(255,224,26,.9)}
        .e650-btn{transition:transform .15s ease,background-color .15s ease,box-shadow .15s ease}
        .e650-btn:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(255,224,26,.25)}
        .e650-btn:active{transform:translateY(0) scale(.98)}
        @keyframes e650-kenburns{0%{transform:scale(1)}100%{transform:scale(1.08)}}
        .e650-kb{animation:e650-kenburns 18s ease-in-out infinite alternate}
        @keyframes e650-marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
        .e650-marquee{animation:e650-marquee 28s linear infinite}
        .e650-marquee:hover{animation-play-state:paused}
        @keyframes e650-pulse{0%,100%{opacity:.35}50%{opacity:.7}}
        .e650-glow{animation:e650-pulse 4s ease-in-out infinite}
        @keyframes e650-hookpulse{0%,100%{box-shadow:0 0 0 rgba(255,224,26,.15)}50%{box-shadow:0 0 26px rgba(255,224,26,.45)}}
        .e650-hook{animation:e650-hookpulse 3s ease-in-out infinite}
      `}</style>

      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2.5 cursor-pointer bg-transparent border-0" data-testid="landing-logo">
            {c.hero?.logo_url && <img src={c.hero.logo_url} alt="The Self-Agency Project logo" className="h-9 w-auto max-w-[200px] object-contain" />}
            {!c.hero?.logo_url && <span className="font-anton text-xl tracking-wide text-white">THE SELF-AGENCY <span className="text-[#FFE01A]">PROJECT</span></span>}
          </button>
          <nav className="hidden md:flex items-center gap-7">
            {navLinks.map(([id, label]) => (
              <button key={id} onClick={() => scrollTo(id)} data-testid={`nav-${id}`}                className="bg-transparent border-0 cursor-pointer text-sm text-[#A1A1AA] hover:text-white uppercase tracking-[0.12em] font-semibold" style={{ transition: "color .15s" }}>
                {label}
              </button>
            ))}
            <button onClick={() => nav("/blog")} data-testid="nav-blog"
              className="bg-transparent border-0 cursor-pointer text-sm text-[#A1A1AA] hover:text-white uppercase tracking-[0.12em] font-semibold" style={{ transition: "color .15s" }}>
              Blog
            </button>
            <button onClick={goApp} data-testid="nav-start-training"
              className="e650-btn bg-[#FFE01A] text-black font-bold uppercase tracking-wide text-sm px-5 py-2.5 cursor-pointer border-0">
              Start Training
            </button>
          </nav>
          <button className="md:hidden bg-transparent border-0 text-white cursor-pointer" onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-menu-toggle">
            {menuOpen ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-black/95 border-t border-white/10 px-5 py-4 flex flex-col gap-4" data-testid="nav-mobile-menu">
            {navLinks.map(([id, label]) => (
              <button key={id} onClick={() => scrollTo(id)} className="bg-transparent border-0 cursor-pointer text-left text-white uppercase tracking-[0.12em] font-semibold">{label}</button>
            ))}
            <button onClick={() => nav("/blog")} data-testid="nav-mobile-blog" className="bg-transparent border-0 cursor-pointer text-left text-white uppercase tracking-[0.12em] font-semibold">Blog</button>
            <button onClick={goApp} className="bg-[#FFE01A] text-black font-bold uppercase px-5 py-3 border-0 cursor-pointer">Start Training</button>
          </div>
        )}
      </header>

      {/* HERO */}
      {c.hero?.enabled !== false && (
        <section className="relative pt-16 e650-grain overflow-hidden" data-testid="hero-section">
          <div className="absolute inset-0 overflow-hidden">
            <img src={c.hero.image_url} alt="Self-defense training in action" className="w-full h-full object-cover e650-kb" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#FFE01A]/10 blur-3xl e650-glow" />
          </div>
          <div className="relative max-w-6xl mx-auto px-5 pt-5 sm:pt-8 pb-14 sm:pb-20">
            {c.sap?.enabled !== false && c.sap?.hero_hook_enabled !== false && (
              <motion.button initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
                onClick={goApp} data-testid="hero-sap-hook"
                className="e650-hook group mb-8 inline-flex items-center gap-3 border border-[#FFE01A]/60 bg-[#FFE01A]/10 hover:bg-[#FFE01A]/20 backdrop-blur px-4 sm:px-5 py-2.5 sm:py-3 cursor-pointer transition-colors duration-200 text-left">
                <Sparkle size={17} weight="fill" color="#FFE01A" className="shrink-0 self-center" />
                <span className="text-[11px] sm:text-[13px] font-bold uppercase tracking-[0.2em] text-[#FFE01A] leading-snug">
                  <span className="block sm:inline">{(c.sap.hero_hook_text || "").split(" ").slice(0, 3).join(" ")}</span>
                  <span className="block sm:inline">{" "}{(c.sap.hero_hook_text || "").split(" ").slice(3).join(" ")}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-white font-bold uppercase text-[12px] sm:text-sm tracking-wide shrink-0">
                  {c.sap.hero_hook_cta}
                  <ArrowRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </motion.button>
            )}
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#FFE01A] mb-5">{c.hero.kicker}</motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="font-anton uppercase text-white leading-[0.92] tracking-tight text-5xl sm:text-6xl lg:text-7xl max-w-3xl" data-testid="hero-title">
              {c.hero.title}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.28 }}
              className="mt-6 max-w-xl text-[#D4D4D8] leading-relaxed text-base sm:text-lg">{c.hero.body}</motion.p>
            {c.hero.members_badge_enabled && (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 }}
                className="inline-flex items-center gap-2 mt-5 border border-white/20 bg-white/5 px-4 py-1.5 text-sm font-semibold" data-testid="members-badge">
                <Star size={15} weight="fill" color="#FFE01A" /> {c.hero.members_text}
              </motion.div>
            )}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.42 }}
              className="flex flex-wrap gap-3 mt-9">
              <button onClick={goApp} data-testid="hero-cta-primary"
                className="e650-btn bg-[#FFE01A] text-black font-bold uppercase tracking-wide px-7 py-4 text-base cursor-pointer border-0">
                {c.hero.cta_primary_label}
              </button>
              <button onClick={() => setBookingOpen(true)} data-testid="hero-cta-book"
                className="e650-btn bg-transparent border border-white/30 hover:border-white text-white font-bold uppercase tracking-wide px-7 py-4 text-base cursor-pointer">
                {c.hero.cta_secondary_label}
              </button>
            </motion.div>
            {c.hero.cta_caption && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }}
                className="mt-4 text-[12px] sm:text-sm text-[#A1A1AA]" data-testid="hero-cta-caption">
                {c.hero.cta_caption}
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* TICKER */}
      {c.ticker?.enabled !== false && c.ticker?.items?.length > 0 && (
        <div className="border-y border-[#FFE01A]/30 bg-[#FFE01A] overflow-hidden py-3 select-none" data-testid="ticker-strip">
          <div className="e650-marquee flex whitespace-nowrap w-max">
            {[...c.ticker.items, ...c.ticker.items, ...c.ticker.items, ...c.ticker.items].map((t, i) => (
              <span key={i} className="font-condensed uppercase font-bold text-black text-lg tracking-wide flex items-center">
                {t}<span className="mx-5 text-black/40">✦</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* THE SELF-AGENCY PROJECT */}
      {c.sap?.enabled !== false && c.sap && (
        <section id="self-agency" ref={sapRef} className="border-t border-[#FFE01A]/30 bg-gradient-to-b from-[#141200] to-[#0A0A0A] e650-grain" data-testid="sap-section">
          <div className="max-w-6xl mx-auto px-5 py-20 flex flex-col md:flex-row items-center gap-10 relative">
            <div className="absolute -top-20 left-1/4 w-72 h-72 rounded-full bg-[#FFE01A]/10 blur-3xl e650-glow pointer-events-none" />
            <Reveal className="flex-1">
              <Kicker>{c.sap.kicker}</Kicker>
              <h2 className="font-anton uppercase text-white leading-[0.95] tracking-tight text-4xl sm:text-5xl">{c.sap.title}</h2>
              <p className="mt-5 text-[#D4D4D8] leading-relaxed max-w-xl">{c.sap.body}</p>
              <p className="mt-3 text-sm text-[#71717A]">{c.sap.note}</p>
            </Reveal>
            <Reveal delay={0.15} className="shrink-0">
              <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} onClick={goApp} data-testid="sap-cta"
                className="e650-btn group bg-[#FFE01A] text-black font-bold uppercase tracking-wide px-8 py-5 text-lg cursor-pointer border-0 inline-flex items-center gap-3">
                {c.sap.cta_label}
                <ArrowRight size={24} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1.5" />
              </motion.button>
            </Reveal>
          </div>
        </section>
      )}

      {/* PROBLEM */}
      {c.problem?.enabled !== false && (
        <section id="problem" className="border-t border-white/10 e650-grain" data-testid="problem-section">
          <div className="max-w-6xl mx-auto px-5 py-24 grid md:grid-cols-2 gap-12 items-center">
            <Reveal>
              <Kicker>{c.problem.kicker}</Kicker>
              <H2>{c.problem.title}</H2>
              {(c.problem.body || "").split("\n").map((p, i) => (
                <p key={i} className="mt-5 text-[#A1A1AA] leading-relaxed">{p}</p>
              ))}
              <div className="flex flex-wrap gap-3 mt-8">
                <button onClick={goApp} data-testid="problem-cta"
                  className="e650-btn bg-[#FFE01A] text-black font-bold uppercase tracking-wide px-6 py-3.5 cursor-pointer border-0">
                  {c.hero.cta_primary_label}
                </button>
                <button onClick={() => setBookingOpen(true)} data-testid="problem-cta-book"
                  className="e650-btn bg-transparent border border-white/30 hover:border-white text-white font-bold uppercase tracking-wide px-6 py-3.5 cursor-pointer">
                  {c.hero.cta_secondary_label}
                </button>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="relative border border-white/10 overflow-hidden group">
              <img src={c.problem.image_url} alt="Training that holds up under pressure" className="w-full h-[420px] object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </Reveal>
          </div>
        </section>
      )}

      {/* SYSTEM PILLARS */}
      {c.system?.enabled !== false && (
        <section id="system" className="border-t border-white/10 bg-[#0D0D0D] e650-grain" data-testid="system-section">
          <div className="max-w-6xl mx-auto px-5 py-24">
            <Reveal>
              <Kicker>{c.system.kicker}</Kicker>
              <H2>{c.system.title}</H2>
            </Reveal>
            <div className="grid md:grid-cols-2 gap-6 mt-12">
              {(c.system.pillars || []).map((p, i) => {
                const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
                return (
                  <Reveal key={i} delay={i * 0.1} className="e650-card border border-white/10 bg-[#121212] p-8" data-testid={`pillar-${i}`}>
                    <div className="w-12 h-12 grid place-items-center bg-[#FFE01A]"><Icon size={24} weight="bold" color="#000" /></div>
                    <h3 className="font-condensed uppercase font-bold text-2xl tracking-tight mt-5 text-white">{p.title}</h3>
                    <p className="text-[#A1A1AA] leading-relaxed mt-3">{p.body}</p>
                  </Reveal>
                );
              })}
            </div>
            <Reveal><p className="mt-10 text-lg text-[#D4D4D8] italic max-w-2xl border-l-2 border-[#FFE01A] pl-5">{c.system.outro}</p></Reveal>
          </div>
        </section>
      )}

      {/* LINEAGE */}
      {c.lineage?.enabled !== false && c.lineage && (
        <section id="lineage" className="border-t border-white/10 e650-grain" data-testid="lineage-section">
          <div className="max-w-6xl mx-auto px-5 py-24">
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <Reveal>
                <Kicker>{c.lineage.kicker}</Kicker>
                <H2>{c.lineage.title}</H2>
                {(c.lineage.body || "").split("\n").map((p, i) => (
                  <p key={i} className="mt-5 text-[#A1A1AA] leading-relaxed">{p}</p>
                ))}
              </Reveal>
              <Reveal delay={0.15}>
                <div className="relative border border-white/10 overflow-hidden group">
                  <img src={c.lineage.image_url} alt="Our lineage" className="w-full h-[340px] object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                {c.lineage.image_caption && <p className="text-xs text-[#71717A] italic mt-2">{c.lineage.image_caption}</p>}
                <div className="mt-6 space-y-0">
                  {(c.lineage.chain || []).map((s, i) => (
                    <div key={i} className="flex gap-4 relative pb-5" data-testid={`lineage-chain-${i}`}>
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 bg-[#FFE01A] mt-1.5 shrink-0" />
                        {i < (c.lineage.chain || []).length - 1 && <div className="w-px flex-1 bg-white/15" />}
                      </div>
                      <div>
                        <div className="text-xs text-[#FFE01A] font-bold tracking-widest uppercase">{s.era}</div>
                        <div className="font-condensed uppercase font-bold text-lg text-white">{s.name}</div>
                        <p className="text-sm text-[#A1A1AA]">{s.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
            <Reveal className="mt-16">
              <h3 className="font-condensed uppercase font-bold text-2xl tracking-tight text-white">{c.lineage.books_heading}</h3>
              <p className="text-[#A1A1AA] mt-2 max-w-3xl">{c.lineage.books_blurb}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 mt-8">
                {(c.lineage.books || []).map((b, i) => (
                  <a key={i} href={b.url} target="_blank" rel="noopener noreferrer" data-testid={`lineage-book-${i}`}
                    className="group block border border-white/10 bg-[#121212] hover:border-[#FFE01A]/60 transition-colors">
                    {b.img ? (
                      <img src={b.img} alt={b.title} className="w-full aspect-[2/3] object-contain bg-white" />
                    ) : (
                      <div className="w-full aspect-[2/3] grid place-items-center p-4 bg-gradient-to-b from-[#1A1A1A] to-[#0D0D0D]">
                        <span className="font-condensed uppercase font-bold text-lg text-center text-white/80 leading-tight">{b.title}</span>
                      </div>
                    )}
                    <div className="p-3">
                      <div className="text-sm font-bold text-white leading-snug">{b.title}</div>
                      <div className="text-xs text-[#A1A1AA] mt-1 leading-snug">{b.tagline}</div>
                      <div className="text-xs text-[#FFE01A] mt-2 font-bold uppercase tracking-wide">View on Amazon →</div>
                    </div>
                  </a>
                ))}
              </div>
              {c.lineage.books_cta && (
                <a href={c.lineage.books_cta_url} target="_blank" rel="noopener noreferrer" data-testid="lineage-books-all"
                  className="inline-block mt-8 e650-btn bg-transparent border border-white/30 hover:border-[#FFE01A] text-white font-bold uppercase tracking-wide px-6 py-3.5">
                  {c.lineage.books_cta}
                </a>
              )}
            </Reveal>
          </div>
        </section>
      )}

      {/* VIDEO (optional) */}
      {c.video?.enabled && (() => {
        const vids = (c.video.videos || []).filter((v) => v?.url);
        if (!vids.length && c.video.video_url) vids.push({ url: c.video.video_url, title: "" });
        if (!vids.length) return null;
        return (
          <section id="video" className="border-t border-white/10" data-testid="video-section">
            <div className="max-w-6xl mx-auto px-5 py-24">
              <Reveal>
                <Kicker>{c.video.kicker}</Kicker>
                <H2>{c.video.title}</H2>
                <p className="mt-4 text-[#A1A1AA] max-w-2xl">{c.video.body}</p>
              </Reveal>
              <div className={`grid gap-6 mt-10 max-w-4xl ${vids.length > 1 ? "sm:grid-cols-2" : ""}`}>
                {vids.map((v, i) => (
                  <Reveal key={i} delay={i * 0.12} className="border border-white/10 max-w-md">
                    <AutoPauseVideo src={v.url} testid={`landing-video-${i}`} />
                    {v.title && <div className="px-4 py-3 font-condensed uppercase font-bold tracking-tight text-white bg-[#121212]">{v.title}</div>}
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* PRICING */}
      {c.pricing?.enabled !== false && (
        <section id="pricing" className="border-t border-white/10 e650-grain" data-testid="pricing-section">
          <div className="max-w-6xl mx-auto px-5 py-24">
            <Reveal>
              <Kicker>{c.pricing.kicker}</Kicker>
              <H2>{c.pricing.title}</H2>
              <p className="mt-4 text-[#A1A1AA]">{c.pricing.subtitle}</p>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-6 mt-12 items-stretch">
              {(c.pricing.cards || []).map((card, i) => (
                <Reveal key={i} delay={i * 0.12} data-testid={`pricing-card-${i}`}
                  className={`e650-card flex flex-col border p-8 ${card.highlight ? "border-[#FFE01A] bg-[#141200]" : "border-white/10 bg-[#121212]"}`}>
                  {card.highlight && <div className="self-start text-[10px] font-bold uppercase tracking-[0.2em] bg-[#FFE01A] text-black px-2.5 py-1 mb-4">Most Popular</div>}
                  <h3 className="font-condensed uppercase font-bold text-2xl tracking-tight text-white">{card.name}</h3>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-anton text-5xl text-[#FFE01A]">{card.price}</span>
                    <span className="text-sm text-[#71717A]">{card.price_note}</span>
                  </div>
                  <p className="text-[#A1A1AA] leading-relaxed mt-4 text-sm">{card.blurb}</p>
                  <ul className="mt-5 space-y-2 flex-1">
                    {(card.features || []).map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-[#D4D4D8]">
                        <span className="text-[#FFE01A] mt-0.5">▸</span>{f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={ctaAction(card.cta)} data-testid={`pricing-cta-${i}`}
                    className={`e650-btn mt-7 font-bold uppercase tracking-wide px-6 py-3.5 cursor-pointer ${card.highlight ? "bg-[#FFE01A] text-black border-0" : "bg-transparent border border-white/25 hover:border-white text-white"}`}>
                    {card.cta_label}
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQS */}
      {c.faqs?.enabled !== false && (
        <section id="faqs" className="border-t border-white/10 bg-[#0D0D0D]" data-testid="faqs-section">
          <div className="max-w-3xl mx-auto px-5 py-24">
            <Reveal>
              <Kicker>{c.faqs.kicker}</Kicker>
              <H2>{c.faqs.title}</H2>
            </Reveal>
            <Reveal delay={0.1}><Accordion type="single" collapsible className="mt-10">
              {(c.faqs.items || []).map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger data-testid={`faq-${i}`} className="text-left text-white hover:text-[#FFE01A] font-semibold text-base">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[#A1A1AA] leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion></Reveal>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {c.testimonials?.enabled !== false && (
        <section id="results" className="border-t border-white/10 e650-grain" data-testid="testimonials-section">
          <div className="max-w-6xl mx-auto px-5 py-24">
            <Reveal>
              <Kicker>{c.testimonials.kicker}</Kicker>
              <H2>{c.testimonials.title}</H2>
              <p className="mt-4 text-[#A1A1AA]">{c.testimonials.subtitle}</p>
            </Reveal>
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6 mt-12 [column-fill:balance]">
              {(c.testimonials.items || []).map((t, i) => (
                <Reveal key={i} delay={(i % 3) * 0.1}>
                  <figure className="e650-card break-inside-avoid border border-white/10 bg-[#121212] p-6 mb-6" data-testid={`testimonial-${i}`}>
                    <div className="flex gap-0.5 mb-3">{[...Array(5)].map((_, s) => <Star key={s} size={14} weight="fill" color="#FFE01A" />)}</div>
                    <blockquote className="text-[#D4D4D8] leading-relaxed text-sm">“{t.quote}”</blockquote>
                    <figcaption className="mt-4 text-[#FFE01A] font-bold text-sm uppercase tracking-wide">— {t.name}</figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTACT */}
      {c.contact?.enabled !== false && (
        <section id="contact" className="border-t border-white/10 bg-[#0D0D0D]" data-testid="contact-section">
          <div className="max-w-6xl mx-auto px-5 py-24 grid md:grid-cols-2 gap-12">
            <Reveal>
              <Kicker>{c.contact.kicker}</Kicker>
              <H2>{c.contact.title}</H2>
              <p className="mt-5 text-[#A1A1AA] leading-relaxed max-w-md">{c.contact.body}</p>
              <div className="flex flex-wrap gap-3 mt-8">
                <a href={phoneHref} data-testid="contact-call" className="e650-btn inline-flex items-center gap-2 bg-[#FFE01A] text-black font-bold uppercase tracking-wide px-6 py-3.5 no-underline">
                  <Phone size={18} weight="bold" /> {c.contact.phone}
                </a>
                <a href={smsHref} data-testid="contact-text" className="e650-btn inline-flex items-center gap-2 border border-white/25 hover:border-white text-white font-bold uppercase tracking-wide px-6 py-3.5 no-underline">
                  <ChatText size={18} weight="bold" /> Text Us
                </a>
              </div>
              <button onClick={() => setBookingOpen(true)} data-testid="contact-book"
                className="e650-btn mt-4 bg-transparent border border-[#FFE01A]/50 hover:border-[#FFE01A] text-[#FFE01A] font-bold uppercase tracking-wide px-6 py-3.5 cursor-pointer block">
                Or Book Online — Pick Your Own Time
              </button>
            </Reveal>
            {c.contact.form_enabled !== false && (
              <Reveal delay={0.15}>
              <form onSubmit={sendContact} className="border border-white/10 bg-[#121212] p-8 flex flex-col gap-4" data-testid="contact-form">
                <div className="font-condensed uppercase font-bold text-xl tracking-tight">Send a Message</div>
                <input data-testid="contact-name" required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-black border border-white/15 focus:border-[#FFE01A] outline-none text-white px-4 py-3 text-sm" />
                <div className="grid grid-cols-2 gap-4">
                  <input data-testid="contact-email" type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-black border border-white/15 focus:border-[#FFE01A] outline-none text-white px-4 py-3 text-sm" />
                  <input data-testid="contact-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-black border border-white/15 focus:border-[#FFE01A] outline-none text-white px-4 py-3 text-sm" />
                </div>
                <textarea data-testid="contact-message" required rows={5} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="bg-black border border-white/15 focus:border-[#FFE01A] outline-none text-white px-4 py-3 text-sm resize-y" />
                <button type="submit" disabled={sending} data-testid="contact-submit"
                  className="e650-btn bg-[#FFE01A] text-black font-bold uppercase tracking-wide px-6 py-3.5 cursor-pointer border-0 disabled:opacity-50">
                  {sending ? "Sending…" : "Send Message"}
                </button>
              </form>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="border-t border-white/10 bg-black" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-5 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-anton text-lg">THE SELF-AGENCY <span className="text-[#FFE01A]">PROJECT</span></div>
            <div className="text-[#A1A1AA] text-xs uppercase tracking-[0.18em] mt-1.5">Brought to you by Elevation650</div>
            <p className="text-[#71717A] text-sm mt-2 max-w-sm">{c.footer?.tagline}</p>
          </div>
          <div className="text-right">
            <p className="text-[#71717A] text-sm">{c.footer?.app_line}</p>
            <button onClick={() => nav("/blog")} data-testid="footer-blog-link"
              className="bg-transparent border-0 text-[#A1A1AA] hover:text-white cursor-pointer uppercase tracking-wide text-sm font-semibold mr-4">
              Blog
            </button>
            <button onClick={goApp} data-testid="footer-app-link"
              className="e650-btn group mt-2 bg-transparent border border-[#FFE01A]/60 hover:border-[#FFE01A] text-[#FFE01A] font-bold uppercase text-sm tracking-wide px-5 py-2.5 cursor-pointer inline-flex items-center gap-2">
              Start Training
              <ArrowRight size={16} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
        <div className="border-t border-white/5 py-4 text-center text-[#52525B] text-xs">
          © {new Date().getFullYear()} Elevation650 · The Self-Agency Project
          <span className="mx-2">·</span>
          <a href="/privacy" className="text-[#71717A] hover:text-white no-underline" data-testid="footer-privacy-link">Privacy</a>
          <span className="mx-2">·</span>
          <a href="/terms" className="text-[#71717A] hover:text-white no-underline" data-testid="footer-terms-link">Terms</a>
          <span className="mx-2">·</span>
          <a href="/privacy-compliance" className="text-[#71717A] hover:text-white no-underline" data-testid="footer-compliance-link">Privacy & Schools</a>
          <span className="mx-2">·</span>
          <a href="/schools" className="text-[#71717A] hover:text-white no-underline" data-testid="footer-schools-link">For Schools</a>
          <span className="mx-2">·</span>
          <button onClick={() => nav("/privacy")} data-testid="footer-privacy-link" className="bg-transparent border-0 p-0 text-[#52525B] hover:text-white cursor-pointer text-xs underline underline-offset-2">Privacy Policy</button>
          <span className="mx-2">·</span>
          <button onClick={() => nav("/terms")} data-testid="footer-terms-link" className="bg-transparent border-0 p-0 text-[#52525B] hover:text-white cursor-pointer text-xs underline underline-offset-2">Terms of Service</button>
        </div>
      </footer>

      {c.sap?.enabled !== false && c.sap?.float_enabled !== false && !floatHidden && (
        <div className="fixed bottom-5 inset-x-0 z-40 flex justify-center md:justify-end md:pr-6 pointer-events-none">
          <motion.button initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.5 }}
            onClick={goApp} data-testid="sap-float-pill"
            className="e650-btn pointer-events-auto bg-[#FFE01A] text-black font-bold uppercase tracking-wide text-[13px] sm:text-sm px-6 py-3.5 rounded-full cursor-pointer border-0 shadow-[0_8px_30px_rgba(255,224,26,0.4)] inline-flex items-center gap-2 whitespace-nowrap">
            <Sparkle size={16} weight="fill" /> {c.sap.float_text} <ArrowRight size={16} weight="bold" />
          </motion.button>
        </div>
      )}

      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} phone={c.contact?.phone} phoneRaw={c.contact?.phone_raw} booking={c.booking} />
    </div>
  );
}
